import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "use-debounce";
import { Form, Modal, Table, TreeSelect } from "antd";
import { useTranslation } from "react-i18next";
import { getAllPtmDescriptionByObjectIdAction } from "../../store/actions";
import { getPtmByIds } from "../../../NRI/RePTMs/store/actions";
import { getTreeAction, clearAction } from "../../../NRI/RePTMs/store/actions";
import CustomInput from "../../../../components/CustomInput";
import CustomButton from "../../../../components/CustomButton";
import DeleteIcon from "../../../../components/Icons/delete.svg";
import Notifications from "../../../../helpers/Notifications";

const EditPtmDescription = ({ descriptions, setDescriptions, objectId, form, formChange }) => {
    const [visible, setVisible] = useState(false);
    const [searchPTMs, setSearchPTMs] = useState(null);
    const [selectedPtm, setSelectedPtm] = useState([]);

    const { tree, list } = useSelector((state) => state.rePtmReducer);
    const { ptmsWithDescription } = useSelector((state) => state.projectReducer);
    const [PTM] = useDebounce(searchPTMs, 500);
    const { t } = useTranslation(["common", "action", "project"]);
    const dispatch = useDispatch();

    const getByIds = (values) => {
        if (selectedPtm.length) {
            dispatch(getPtmByIds(values));
        }
    };

    const getTree = (values) => {
        dispatch(getTreeAction(values));
    };

    useEffect(() => {
        return () => {
            dispatch(clearAction());
        };
    }, []);

    useEffect(() => {
        if (objectId)
            dispatch(getAllPtmDescriptionByObjectIdAction({ id: objectId }));
    }, [objectId]);

    useEffect(() => {
        getTree({ name: PTM?.trim(), level: 2 });
    }, [PTM]);

    useEffect(() => {
        if (ptmsWithDescription?.length) {
            form.setFieldsValue({
                descriptions: Object.fromEntries(
                    ptmsWithDescription.map((p) => [p.id, p.description])
                ),
            });

            setDescriptions(
                ptmsWithDescription?.map((p) => {
                    return {
                        ...p,
                        isParent: p.description === null,
                        description: p.description,
                    };
                }) ?? []
            );

            setSelectedPtm(
                ptmsWithDescription?.flatMap((p) =>
                    p.description === null ? [] : p.id
                )
            );
        }
    }, [ptmsWithDescription]);

    useEffect(() => {
        let ptms = list ? [...list, ...findParents(list)] : [];

        ptms = ptms?.map((p) => {
            const previousDescription = ptmsWithDescription?.find(
                ({ id }) => id === p.id
            );
            
            return {
                ...p,
                description: previousDescription ? previousDescription.description : "",
            };
        });

        const sortedPtms = ptms.sort((a, b) => a.code.localeCompare(b.code));

        setDescriptions(sortedPtms);
        formChange(undefined, sortedPtms)
    }, [list]);

    const findParents = (ptms) => {
        let parents = ptms?.flatMap((ptm, _, arr) => {
            if (arr.find((p) => p.id === ptm.parentId)) {
                return [];
            }

            const parent = tree.find((t) => t.id === ptm.parentId);
            if (parent)
                return {
                    id: parent.id,
                    code: parent.code,
                    name: parent.name,
                    isParent: parent.parent.id === null,
                };
            return [];
        });

        parents = [...new Map(parents.map((item) => [item.id, item])).values()]; //remove duplicate
        return parents;
    };

    const showPTM = () => {
        if (selectedPtm.length) {
            getByIds({ ids: selectedPtm });
            setVisible(false);
        } else {
            Notifications.errorNotice(`${t("action:choose")} ${t("common:ptm")}`, null, 20);
        }
    };

    const deleteHandler = (ptmId) => {
        const ptm = descriptions.find(d => d.id == ptmId)
        const isLastChild = descriptions.filter(d => d.parentId == ptm.parentId).length == 1;

        setSelectedPtm(selectedPtm.filter((d) => d != ptmId));
        const sortDescriptions = descriptions.filter((d) => d.id != ptmId && (!isLastChild || d.id != ptm.parentId));
        setDescriptions(sortDescriptions);
        formChange(undefined, sortDescriptions)
    };

    const columns = [
        {
            dataIndex: "name",
            title: t("project:characteristic"),
            key: "name",
            width: "35%",
        },
        {
            title: `${t("project:description")} ${t("ptm")}`,
            dataIndex: "id",
            key: "description",
            render: (ptmId, ptm) =>
                !ptm.isParent && (
                    <Form.Item
                        name={["descriptions", [ptmId]]}
                        className="description-form"
                        rules={[
                            {
                                required: true,
                                message: `${t("action:enter")} ${ptm.name}`,
                            },
                        ]}
                    >
                        <CustomInput
                            type="textarea"
                            autoSize
                            className="description"
                            placeholder={`${t("action:enter")} ${t(
                                "project:description"
                            ).toLocaleLowerCase()} ${t("ptm")}`}
                            onChange={(e) => {
                                setDescriptions(
                                    descriptions.map((d) =>
                                        d.id === ptmId
                                            ? {
                                                ...d,
                                                description:
                                                    e.target.value.trim(),
                                            }
                                            : d
                                    )
                                );
                            }}
                        />
                    </Form.Item>
                ),
        },
        {
            dataIndex: "id",
            key: "manage",
            width: "3%",
            render: (ptmId, ptm) =>
                !ptm.isParent ? (
                    <img alt=""
                        src={DeleteIcon}
                        className="delete-icon"
                        title={t("action:delete")}
                        onClick={() => deleteHandler(ptmId)}
                    />
                ) : null,
        },
    ];

    return (
        <div className="edit-ptm-description">
            <p className="edit-ptm-description-title">
                {t("project:objCharacteristics")}
            </p>

            <div className="edit-ptm-description-manage">
                <CustomButton
                    type="green"
                    className="add-btn"
                    onClick={() => {
                        setVisible(true);
                    }}
                >
                    <div>
                        <span>{`${t("project:addPtmDescription")}`}</span>
                    </div>
                </CustomButton>
            </div>

            {descriptions?.length > 0 ? (
                <Table
                    dataSource={descriptions}
                    rowKey={(item) => item.id}
                    pagination={false}
                    columns={columns}
                    showHeader={false}
                    rowClassName={(record) =>
                        record?.isParent ? "description-group-title" : ""
                    }
                    scroll={{ x: 1100, y: 700 }}
                />
            ) : (
                t("project:noData")
            )}

            <Modal
                footer={[
                    <CustomButton
                        type="green"
                        key="appleDesc"
                        onClick={showPTM}
                    >
                        {t("apply")}
                    </CustomButton>,
                ]}
                onCancel={showPTM}
                title={`${t("project:addPtmDescription")}`}
                open={visible}
                width={900}
            >
                {tree && (
                    <TreeSelect
                        allowClear
                        multiple
                        treeCheckable
                        showCheckedStrategy="SHOW_CHILD"
                        placeholder={`${t("action:choose")} ${t("ptm")}`}
                        treeData={tree}
                        value={selectedPtm}
                        fieldNames={{
                            label: "name",
                            value: "id",
                            key: "id",
                            children: "children",
                        }}
                        style={{ width: "100%" }}
                        height={600}
                        onChange={(keys) => setSelectedPtm(keys)}
                        onSearch={(name) => setSearchPTMs(name)}
                    />
                )}
            </Modal>
        </div>
    );
};

export default EditPtmDescription;
